import { useMutation } from "@tanstack/react-query";
import { BACKEND } from "../../config/backend";
import { generateSecretFromObject } from "../../utils/cryptoUtils";
import { axiosPublic } from "../../axios-config/axiosPublic";

const SUPPORT_TICKET_URL = `${BACKEND}/api/v1/support-ticket`;

/**
 * Creates a support ticket with secret validation
 * The secret must be generated from: email, phone, subject, description (in this order)
 * This matches the backend validation in generalSupportTicketHandler.js
 * 
 * @param {Object} ticketData - The ticket data
 * @param {string} ticketData.email - User email
 * @param {string} ticketData.phone - User phone
 * @param {string} ticketData.subject - Ticket subject
 * @param {string} ticketData.description - Ticket description
 * @param {File[]} [ticketData.files] - Optional file attachments
 * @returns {Promise} - Axios response
 */
const createSupportTicket = async (ticketData) => {
    const { email, phone, subject, description, files = [] } = ticketData;

    // Generate secret from the required fields in exact order (matching backend)
    const secretData = {
        email,
        phone,
        subject,
        description
    };
    const secret = await generateSecretFromObject(secretData);

    // Create FormData for multipart/form-data request
    const formData = new FormData();
    formData.append('email', email);
    formData.append('phone', phone);
    formData.append('subject', subject);
    formData.append('description', description);
    formData.append('secret', secret);

    // Append files if provided
    if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
            formData.append('files', files[i]);
        }
    }

    return axiosPublic.post(SUPPORT_TICKET_URL, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
};

/**
 * Custom hook for creating support tickets with TanStack Query
 * Provides mutation state (isLoading, isSuccess, isError) automatically
 * 
 * @returns {Object} TanStack Query mutation object
 */
export const useCreateSupportTicket = () => {
    return useMutation({
        mutationFn: createSupportTicket
    });
};

